/**
 * A reply arrives twice: once as plain `text`, and once as `text_marked` with
 * every record it names replaced by `{{type:id}}`. The key inside the braces is
 * the key into the turn's `entities`, and that entity is what the front end
 * turns into a link.
 *
 * Nothing here decides what a record *is* — that comes from the backend. It
 * only decides where a record lives in this app.
 */

// The capture group is what keeps the marks in the result: `split` drops
// delimiters unless they are captured.
const ENTITY_CHUNK = /(\{\{[a-z_]+:\d+\}\})/
const ENTITY_KEY = /^\{\{([a-z_]+:\d+)\}\}$/

const HREF_BY_TYPE = {
    wig: ({ id }) => `/orders/${id}`,
    // The client card is a dialog rather than a route, so the clients screen
    // opens it from the query on arrival.
    client: ({ id }) => `/clients-list?client=${id}`,
    // The calendar takes no date, so this lands on it and no further.
    meeting: () => '/meetings-calendar',
}

/**
 * Where an entity opens, or `null` when it opens nowhere: a deleted record, or
 * a type this app has no screen for. Both cases are shown as inert text rather
 * than as a link into a 404.
 */
export const entityHref = (entity) => {
    if (!entity || entity.deleted) return null

    return HREF_BY_TYPE[entity.type]?.(entity) || null
}

/**
 * Split a marked reply into the pieces a bubble draws: `{ text }` for the words
 * between the marks, `{ key, entity }` for each mark that resolves.
 *
 * Nothing to work with returns nothing — the caller falls back to the plain
 * text, which is the shape of every reply that names no records at all. Marks
 * with no entities beside them count as nothing to work with: better the whole
 * sentence plain than a sentence full of visible braces.
 */
export const readSegments = (marked, entities) => {
    if (!marked || !entities) return []

    return marked
        .split(ENTITY_CHUNK)
        .filter((chunk) => chunk !== '')
        .map((chunk) => {
            const key = chunk.match(ENTITY_KEY)?.[1]
            const entity = key ? entities?.[key] : null

            // The backend promises it never marks a key it does not send. If
            // one ever goes missing the mark is shown as it arrived rather than
            // swallowed, so the gap is visible instead of silent.
            return entity ? { key, entity } : { text: chunk }
        })
}

/**
 * The wig refs a reply asks to be drawn as cards. `blocks` is the backend
 * saying "this part is a list", which is the only reason a wig becomes a card
 * rather than a chip in the sentence.
 */
export const readCardRefs = (blocks) =>
    (blocks || [])
        .filter((block) => block?.kind === 'wig_list')
        .flatMap((block) => block.refs || [])

/**
 * Drop the lines the cards replace.
 *
 * A list reply says each row twice — once in prose, once in `blocks` — so the
 * line naming a wig that has a card of its own is dropped and the intro above
 * it is kept. Nothing is dropped when there are no cards.
 */
export const dropCardedLines = (marked, refs) => {
    if (!marked || !refs.length) return marked

    return marked
        .split('\n')
        .filter((line) => !refs.some((ref) => line.includes(`{{${ref}}}`)))
        .join('\n')
        .trim()
}

/**
 * What the list is a part of: `{ shown, total }`, or `null` when the block held
 * everything and there is nothing to admit. The count comes from the block
 * rather than from the cards — the backend measured it, we did not.
 */
export const readListTotals = (blocks) => {
    const lists = (blocks || []).filter((block) => block?.kind === 'wig_list')

    const shown = lists.reduce((sum, block) => sum + (block.shown || 0), 0)
    const total = lists.reduce((sum, block) => sum + (block.total || 0), 0)

    return total > shown ? { shown, total } : null
}
